import { useCallback, useEffect, useState } from 'react';
import { isElectronEnvironment } from '@/hooks/useElectronFileSystem';
import { normalizeDocumentPath } from '@/utils/normalizeDocumentPath';
import { sanitizeFolderName } from '@/utils/sanitizeFolderName';

const FOLDER_KEY = 'pierino.scanner.folder';
const REGISTRY_KEY = 'pierino.scanner.associated';
const POLL_MS = 4000;

const SKIP_EXT = new Set(['tmp', 'part', 'crdownload']);

export interface ScannerFile {
  name: string;
  size: number;
  lastModified: number;
  fullPath: string;
  key: string;
}

interface ScannerSnapshot {
  isElectron: boolean;
  folder: string | null;
  files: ScannerFile[];
  error: string | null;
  pendingCount: number;
  associatedKeys: string[];
}

function loadFolder(): string | null {
  try {
    const value = localStorage.getItem(FOLDER_KEY);
    return value && value.trim() ? value : null;
  } catch {
    return null;
  }
}

function loadRegistry(): string[] {
  try {
    const raw = localStorage.getItem(REGISTRY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === 'string');
  } catch {
    return [];
  }
}

function saveRegistry(keys: string[]) {
  localStorage.setItem(REGISTRY_KEY, JSON.stringify(keys));
}

export function scannerFileKey(name: string, size: number, lastModified: number): string {
  return `${name}\u0000${size}\u0000${Math.round(lastModified)}`;
}

function isSkippedName(name: string): boolean {
  if (name.startsWith('~')) return true;
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return false;
  return SKIP_EXT.has(name.slice(dot + 1).toLowerCase());
}

let snapshot: ScannerSnapshot = {
  isElectron: false,
  folder: null,
  files: [],
  error: null,
  pendingCount: 0,
  associatedKeys: [],
};

const listeners = new Set<() => void>();
let pollTimer: number | null = null;

function emit() {
  listeners.forEach((listener) => listener());
}

function refreshNow() {
  const isElectron = isElectronEnvironment();
  const folder = loadFolder();
  const associatedKeys = loadRegistry();
  const associated = new Set(associatedKeys);

  if (!isElectron || !folder) {
    snapshot = {
      isElectron,
      folder,
      files: [],
      error: null,
      pendingCount: 0,
      associatedKeys,
    };
    emit();
    return;
  }

  const efs = window.electronFS;
  if (!efs) {
    snapshot = { isElectron: false, folder, files: [], error: null, pendingCount: 0, associatedKeys };
    emit();
    return;
  }

  try {
    if (!efs.directoryExists(folder)) {
      snapshot = {
        isElectron,
        folder,
        files: [],
        error: 'Cartella non trovata. Scegline un’altra.',
        pendingCount: 0,
        associatedKeys,
      };
      emit();
      return;
    }

    const raw = efs.readDirectory(folder);
    const files: ScannerFile[] = [];
    for (const entry of raw) {
      if (entry.kind !== 'file' || isSkippedName(entry.name)) continue;
      const size = entry.size ?? 0;
      const lastModified = entry.lastModified ?? 0;
      files.push({
        name: entry.name,
        size,
        lastModified,
        fullPath: efs.joinPath(folder, entry.name),
        key: scannerFileKey(entry.name, size, lastModified),
      });
    }
    files.sort((a, b) => b.lastModified - a.lastModified || a.name.localeCompare(b.name, 'it'));
    snapshot = {
      isElectron,
      folder,
      files,
      error: null,
      pendingCount: files.filter((file) => !associated.has(file.key)).length,
      associatedKeys,
    };
  } catch (err) {
    snapshot = {
      isElectron,
      folder,
      files: [],
      error: err instanceof Error ? err.message : 'Lettura della cartella non riuscita',
      pendingCount: 0,
      associatedKeys,
    };
  }
  emit();
}

function ensurePoll() {
  if (pollTimer != null) return;
  refreshNow();
  pollTimer = window.setInterval(() => {
    if (document.visibilityState !== 'visible') return;
    refreshNow();
  }, POLL_MS);
}

export function markScannerAssociated(keys: string[]) {
  if (keys.length === 0) return;
  const next = new Set(loadRegistry());
  for (const key of keys) next.add(key);
  saveRegistry([...next]);
  refreshNow();
}

export function useScannerInbox() {
  const [state, setState] = useState(snapshot);

  useEffect(() => {
    const listener = () => setState(snapshot);
    listeners.add(listener);
    ensurePoll();
    listener();
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const pickFolder = useCallback(async () => {
    const picked = await window.electronDialog?.pickDirectory(loadFolder() || undefined);
    if (!picked) return;
    localStorage.setItem(FOLDER_KEY, picked);
    refreshNow();
  }, []);

  return {
    ...state,
    pickFolder,
    refresh: refreshNow,
  };
}

export type ConflictChoice = 'overwrite' | 'rename' | 'cancel';

export interface CopyScannerFileInput {
  sourcePath: string;
  fileName: string;
  documentPath: string;
  subfolder?: string;
  onConflict: (existingName: string) => Promise<ConflictChoice>;
}

function splitFileName(name: string): { base: string; ext: string } {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return { base: name, ext: '' };
  return { base: name.slice(0, dot), ext: name.slice(dot) };
}

function nextFreeName(
  efs: NonNullable<Window['electronFS']>,
  dir: string,
  name: string
): string {
  const { base, ext } = splitFileName(name);
  for (let n = 2; n < 1000; n += 1) {
    const candidate = `${base} (${n})${ext}`;
    if (!efs.pathExists(efs.joinPath(dir, candidate))) return candidate;
  }
  throw new Error('Troppi file con lo stesso nome nella cartella attività');
}

/** Copia un file dalla inbox nella cartella attività. Non cancella l’originale. */
export async function copyScannerFileToActivity(input: CopyScannerFileInput): Promise<void> {
  const efs = window.electronFS;
  if (!efs) throw new Error('Disponibile solo nell’app Studio Gori');

  const basePath = normalizeDocumentPath(input.documentPath) || input.documentPath;
  if (!efs.directoryExists(basePath)) {
    throw new Error('Cartella della pratica non raggiungibile');
  }

  let targetDir = basePath;
  if (input.subfolder) {
    const safeName = sanitizeFolderName(input.subfolder);
    targetDir = efs.joinPath(basePath, safeName);
    if (!efs.directoryExists(targetDir)) {
      efs.createDirectory(targetDir);
    }
  }

  let destName = input.fileName;
  const existingPath = efs.joinPath(targetDir, destName);
  if (efs.pathExists(existingPath)) {
    const choice = await input.onConflict(destName);
    if (choice === 'cancel') {
      throw new Error('__CANCEL__');
    }
    if (choice === 'rename') {
      destName = nextFreeName(efs, targetDir, destName);
    }
  }

  const buffer = efs.readFile(input.sourcePath);
  efs.writeFile(efs.joinPath(targetDir, destName), buffer);
}
